import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { xeroAPI } from "../../lib/api";
import { formatAUD, frequencyLabel } from "../../lib/billing";
import { RefreshCw, Link2, Unlink, Settings, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function SettingsForm({ status, onSaved }) {
  const [clientId, setClientId] = useState(status?.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const redirectUri = `${window.location.origin}/api/xero/callback`;

  const save = async () => {
    setSaving(true);
    try {
      await xeroAPI.saveSettings({ client_id: clientId, client_secret: clientSecret });
      toast.success("Xero settings saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="xero-settings-form">
      <div className="text-sm text-slate-600 bg-blue-50 border border-blue-200 rounded p-3 space-y-1">
        <p className="font-medium text-slate-800">One-time setup:</p>
        <p>1. Go to <b>developer.xero.com</b> → New App → type "Web app"</p>
        <p>2. Set the redirect URI to: <code className="bg-white px-1 py-0.5 rounded border text-xs break-all">{redirectUri}</code></p>
        <p>3. Copy the Client ID and generate a Client Secret, paste them below</p>
      </div>
      <div>
        <Label>Client ID</Label>
        <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Xero app Client ID" data-testid="xero-client-id-input" />
      </div>
      <div>
        <Label>Client Secret</Label>
        <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Xero app Client Secret" data-testid="xero-client-secret-input" />
      </div>
      <Button onClick={save} disabled={saving || !clientId.trim() || !clientSecret.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="xero-save-settings-button">
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}

function PreviewTable({ invoices, customers, selections, setSelections }) {
  const setSel = (xeroId, patch) => setSelections((s) => ({ ...s, [xeroId]: { ...s[xeroId], ...patch } }));
  return (
    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="xero-preview-list">
      {invoices.map((inv) => {
        const sel = selections[inv.xero_id] || {};
        const customerId = sel.customer_id || "";
        return (
          <div key={inv.xero_id} className={`border rounded-lg p-3 ${customerId ? "border-slate-200 bg-white" : "border-amber-200 bg-amber-50"}`} data-testid={`xero-invoice-${inv.xero_id}`}>
            <div className="flex items-start gap-3">
              <Checkbox
                checked={!!sel.include}
                disabled={!customerId}
                onCheckedChange={(v) => setSel(inv.xero_id, { include: !!v })}
                className="mt-1"
                data-testid={`xero-include-${inv.xero_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800">{inv.contact_name}</span>
                  <Badge variant="secondary" className="text-xs">{inv.schedule_label}</Badge>
                  <span className="text-sm text-slate-500">{formatAUD(inv.total)}</span>
                  {inv.next_renewal_date && (
                    <span className="text-xs text-cyan-700" data-testid={`xero-next-renewal-${inv.xero_id}`}>
                      next renewal {new Date(inv.next_renewal_date).toLocaleDateString("en-AU")}
                    </span>
                  )}
                  {!inv.matched_customer_id && !sel.customer_id && (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Unmatched
                    </Badge>
                  )}
                </div>
                {inv.note && <p className="text-xs text-amber-600 mt-0.5">{inv.note}</p>}
                <div className="mt-1.5 space-y-0.5">
                  {inv.services.map((s, i) => (
                    <p key={i} className="text-xs text-slate-600">
                      • {(parseFloat(s.quantity) || 1) > 1 ? `${parseFloat(s.quantity)} × ` : ""}{s.service_name} — <b>{formatAUD(s.price)}</b> / {frequencyLabel(s.frequency)}
                    </p>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-slate-500 shrink-0">Update profile of:</span>
                  <Select value={customerId} onValueChange={(v) => setSel(inv.xero_id, { customer_id: v, include: true })}>
                    <SelectTrigger className="h-8 text-sm max-w-xs" data-testid={`xero-customer-select-${inv.xero_id}`}>
                      <SelectValue placeholder="Select CRM customer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function XeroSync({ open, onOpenChange, customers, onApplied }) {
  const [status, setStatus] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [selections, setSelections] = useState({});
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await xeroAPI.status();
      setStatus(res.data);
    } catch {
      toast.error("Failed to load Xero status");
    }
  }, []);

  useEffect(() => {
    if (open) {
      setInvoices(null);
      setSelections({});
      setShowSettings(false);
      loadStatus();
    }
  }, [open, loadStatus]);

  const connect = async () => {
    try {
      const res = await xeroAPI.connectUrl(window.location.origin);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start Xero connection");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect from Xero?")) return;
    await xeroAPI.disconnect();
    toast.success("Disconnected from Xero");
    loadStatus();
  };

  const fetchPreview = async () => {
    setBusy(true);
    try {
      const res = await xeroAPI.preview();
      setInvoices(res.data.invoices);
      const sel = {};
      res.data.invoices.forEach((inv) => {
        sel[inv.xero_id] = { include: !!inv.matched_customer_id, customer_id: inv.matched_customer_id || "" };
      });
      setSelections(sel);
      if (res.data.invoices.length === 0) toast.info("No active repeating invoices found in Xero");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch from Xero");
      if (err.response?.status === 401) loadStatus();
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const items = Object.entries(selections)
      .filter(([, s]) => s.include && s.customer_id)
      .map(([xero_id, s]) => ({ xero_id, customer_id: s.customer_id }));
    if (items.length === 0) {
      toast.error("Select at least one invoice with a customer");
      return;
    }
    setBusy(true);
    try {
      const res = await xeroAPI.apply(items);
      const ok = res.data.results.filter((r) => !r.error);
      const failed = res.data.results.filter((r) => r.error);
      const added = ok.reduce((s, r) => s + r.added, 0);
      const updated = ok.reduce((s, r) => s + r.updated, 0);
      toast.success(`Updated ${ok.length} billing profile(s): ${added} services added, ${updated} updated`);
      if (failed.length) toast.error(`${failed.length} failed: ${failed.map((f) => f.error).join("; ")}`);
      onApplied?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply import");
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = Object.values(selections).filter((s) => s.include && s.customer_id).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="xero-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[#13b5ea]" /> Xero — Import Repeating Invoices
          </DialogTitle>
          <DialogDescription>
            Imports your Xero repeating invoices and updates client billing profiles (merge: prices and frequency updated, your OTS costs untouched).
          </DialogDescription>
        </DialogHeader>

        {!status ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading...</p>
        ) : !status.configured || showSettings ? (
          <SettingsForm status={status} onSaved={() => { setShowSettings(false); loadStatus(); }} />
        ) : !status.connected ? (
          <div className="text-center py-6 space-y-3" data-testid="xero-not-connected">
            <p className="text-sm text-slate-600">Settings saved. Now authorise access to your Xero organisation.</p>
            <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-2.5 text-left" data-testid="xero-redirect-hint">
              <p className="font-medium text-slate-800 mb-1">⚠️ Before connecting, make sure this exact redirect URI is added to your Xero app (developer.xero.com → your app → Configuration → Redirect URIs):</p>
              <code className="bg-white px-1.5 py-0.5 rounded border block break-all select-all">{`${window.location.origin}/api/xero/callback`}</code>
              <p className="mt-1 text-slate-500">Each domain (preview and your live site) needs its own entry. Otherwise Xero shows "Invalid redirect_uri".</p>
            </div>
            <Button onClick={connect} className="bg-[#13b5ea] hover:bg-[#0fa3d3]" data-testid="xero-connect-button">
              <Link2 className="w-4 h-4 mr-2" /> Connect to Xero
            </Button>
            <div>
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} data-testid="xero-edit-settings-button">
                <Settings className="w-4 h-4 mr-1" /> Edit credentials
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="xero-connected-info">
                  <CheckCircle2 className="w-4 h-4" /> Connected to <b>{status.tenant_name || "Xero"}</b>
                </p>
                {status.last_auto_sync && (
                  <p className="text-xs text-slate-500 mt-0.5" data-testid="xero-last-autosync">
                    Weekly auto-sync last ran {new Date(status.last_auto_sync.date).toLocaleString("en-AU")} — {status.last_auto_sync.status}
                    {status.last_auto_sync.profiles_updated != null && ` (${status.last_auto_sync.profiles_updated} profile(s) updated)`}
                  </p>
                )}
              </div>              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="xero-settings-button">
                  <Settings className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={disconnect} data-testid="xero-disconnect-button">
                  <Unlink className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={fetchPreview} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="xero-fetch-button">
                  <RefreshCw className={`w-4 h-4 mr-1 ${busy && !invoices ? "animate-spin" : ""}`} /> Fetch Repeating Invoices
                </Button>
              </div>
            </div>

            {invoices && invoices.length > 0 && (
              <>
                <PreviewTable invoices={invoices} customers={customers} selections={selections} setSelections={setSelections} />
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button onClick={apply} disabled={busy || selectedCount === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="xero-apply-button">
                    {busy ? "Applying..." : `Update ${selectedCount} Billing Profile(s)`}
                  </Button>
                </div>
              </>
            )}
            {invoices && invoices.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No active repeating invoices found in Xero.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
